// Singleton (window.SupercastUploadManager) that owns episode uploads end-to-end: the dedicated
// Worker (episode_upload_worker.js) driving the TUS byte transfer, every Rails HTTP call
// (presigned_upload, log_upload, the commit PATCH - the Worker has no cookie/CSRF story so it
// can't make these itself), and an in-memory registry of in-flight/recently-finished uploads.
//
// Lives at module scope, not inside a Stimulus controller, so it survives Turbo morph navigation
// (only #page-main is replaced - see app/views/layouts/base.html.erb) and keeps transferring
// after the upload modal closes or the creator navigates away.
//
// Must be loaded via a side-effect import from application.js, the esbuild entry actually served
// on every page - "dashboard" in `<% scripts "dashboard" %>` resolves to the legacy,
// non-esbuild app/assets/javascripts/dashboard.js and is NOT a safe place to wire this in.

import { csrfToken } from "../utils/json_request";

function csrfFetch(url, args) {
  return fetch(url, { ...args, credentials: "include", headers: { ...args.headers, "X-CSRF-Token": csrfToken() } });
}

const POST_VIDEO_FAILED_STATUSES = ["error", "presigned_upload_failed"];

const WORKER_SRC_ATTRIBUTE = "episodeUploadWorkerSrc";

function workerSrc() {
  const el = document.querySelector(`[data-${dasherize(WORKER_SRC_ATTRIBUTE)}]`);
  return el ? el.dataset[WORKER_SRC_ATTRIBUTE] : "/assets/episode_upload_worker.js";
}

function dasherize(camelCase) {
  return camelCase.replace(/([A-Z])/g, "-$1").toLowerCase();
}

// Maps a provider's credentials blob to the value that should be persisted as podcast_video/podcast_audio
const PROVIDER_MEDIA_ID_EXTRACTORS = {
  bunny: (credentials) => credentials.video_id,
};

function mediaIdFor(provider, credentials) {
  const extractor = PROVIDER_MEDIA_ID_EXTRACTORS[provider] || PROVIDER_MEDIA_ID_EXTRACTORS.bunny;
  return extractor(credentials);
}

// An upload is an episode's media by default. kind: "post_video" is an inline video in an email/post
// (Dashboard::Shared::PostVideosController): same worker and multipart transfer, but its own endpoints,
// no episode and no UploadLog. Such a record also carries { postId, blockId, videosPath, label, href },
// so the editor's block can find its upload again and the status widget can name it and link back.
const POST_VIDEO = "post_video";

function isPostVideo(upload) {
  return upload.kind === POST_VIDEO;
}

async function jsonRequest(url, { method = "POST", body } = {}) {
  const response = await csrfFetch(url, {
    method,
    headers: { Accept: "application/json", ...(body ? { "Content-Type": "application/json" } : {}) },
    body: body ? JSON.stringify(body) : undefined,
  });
  let data = {};
  try {
    data = await response.json();
  } catch (error) {
    // empty body
  }
  return { response, data };
}

function postVideoMemberUrl(upload) {
  return `${upload.videosPath}/${upload.externalId}`;
}

function generateLocalId() {
  if (window.crypto && typeof window.crypto.randomUUID === "function") {
    return window.crypto.randomUUID();
  }
  return `upload-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function logUploadState(episodeId, uploadLogId, state, errorMessage = null) {
  if (!uploadLogId || !episodeId) return;

  const formData = new FormData();
  formData.append("state", state);
  formData.append("timestamp", new Date().toISOString());
  formData.append("error_message", errorMessage);

  csrfFetch(`/dashboard/channels/episodes/${episodeId}/log_upload?upload_log_id=${uploadLogId}`, {
    method: "POST",
    headers: { Accept: "application/json" },
    body: formData,
  });
}

class EpisodeUploadManager {
  constructor() {
    this.worker = null;
    this.workerPromise = null;
    // Map<localId, upload record>. Records are kept (including the `file` reference) even after
    // completion/error, until something explicitly evicts them, so `retry()` can resume without
    // re-prompting the creator for a file (no true byte-level resume - a retry always re-fetches
    // fresh Bunny credentials and restarts the transfer from byte 0).
    this.uploads = new Map();
    this.listeners = new Set();
  }

  // Lazily creates the dedicated Worker, so the singleton itself is cheap to construct and no
  // Worker/network activity happens until an upload starts.
  // Returns a Promise<Worker|null> rather than a Worker synchronously because
  // workerSrc() resolves to an absolute URL - workaround is to fetch the script's text,
  // wrap it in a same-origin `Blob`, and construct the Worker from that blob URL instead.
  ensureWorker() {
    if (this.workerPromise) return this.workerPromise;

    this.workerPromise = this._createWorker().catch((error) => {
      // eslint-disable-next-line no-console
      console.error("SupercastUploadManager: failed to load episode_upload_worker.js", error);
      this.workerPromise = null;
      this._dispatchToast("error", "Couldn't start the upload worker. Please retry.");
      return null;
    });

    return this.workerPromise;
  }

  async _createWorker() {
    const response = await fetch(workerSrc());
    if (!response.ok) {
      throw new Error(`Failed to fetch episode_upload_worker.js: ${response.status}`);
    }

    const script = await response.text();
    const blobUrl = URL.createObjectURL(new Blob([script], { type: "application/javascript" }));

    this.worker = new Worker(blobUrl);
    this.worker.addEventListener("message", (event) => this._handleWorkerMessage(event.data || {}));
    this.worker.addEventListener("error", (event) => this._handleWorkerError(event));
    return this.worker;
  }

  // An uncaught error anywhere in the worker's script (e.g. failing to import tus-js-client, or
  // a runtime error thrown mid-transfer) surfaces here instead of at the call site that
  // triggered it - without this handler it was being silently swallowed, leaving affected
  // uploads stuck in "uploading" forever with no error state, no toast, and no commit ever
  // happening. Treat the whole worker as dead (a top-level script error can leave it unable to
  // process any further postMessage calls) so the next start()/retry() spins up a fresh one.
  _handleWorkerError(event) {
    // eslint-disable-next-line no-console
    console.error("SupercastUploadManager: episode_upload_worker.js threw an uncaught error", event.message, event);

    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
      this.workerPromise = null;
    }

    this.uploads.forEach((upload, localId) => {
      if (
        upload.state !== "uploading" &&
        upload.state !== "processing" &&
        upload.state !== "transcoding"
      )
        return;

      this._upsert(localId, { state: "error", errorMessage: "Upload failed unexpectedly - please retry" });
      logUploadState(upload.episodeId, upload.uploadLogId, "error", event.message || "worker error");
      this._notify({ type: "upload-error", upload: this.uploads.get(localId) });
    });

    this._dispatchToast("error", "An upload failed unexpectedly. Please retry.");
  }

  subscribe(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  getAllUploads() {
    return Array.from(this.uploads.values()).map((upload) => ({ ...upload }));
  }

  // O(1) single-record lookup - prefer this over getAllUploads().find(...) when only one
  // upload's record is needed (getAllUploads() copies every tracked record).
  getUpload(localId) {
    const upload = this.uploads.get(localId);
    return upload ? { ...upload } : null;
  }

  getUploadsForEpisode(episodeId) {
    if (!episodeId) return [];

    const matches = [];
    this.uploads.forEach((upload) => {
      if (String(upload.episodeId) === String(episodeId)) matches.push({ ...upload });
    });
    return matches;
  }

  // Starts a brand-new upload. Returns the `localId` synchronously (registered immediately, in
  // the "uploading" state) so a caller can reflect "an upload just started" in the UI (e.g.
  // un-hiding the Publish button) without waiting on any network round-trip. The
  // presigned_upload fetch + worker handoff happen fire-and-forget internally; episodeId
  // resolution (relevant the first time a brand-new episode is created from this upload) is
  // surfaced asynchronously through `subscribe()`.
  start({ file, episodeId, mediaType, dynamic, triggerEvent, duration, height, width, durationRead, kind, postId, blockId, videosPath, label, href }) {
    const localId = generateLocalId();

    this._upsert(localId, {
      localId,
      kind: kind || "episode",
      postId,
      blockId,
      videosPath,
      label,
      href,
      episodeId: episodeId || null,
      uploadLogId: null,
      file,
      mediaType,
      duration,
      height,
      width,
      durationRead,
      state: "uploading",
      bytesUploaded: 0,
      bytesTotal: file ? file.size : 0,
      errorMessage: null,
    });
    this._notify({ type: "start", upload: this.uploads.get(localId) });

    if (kind === POST_VIDEO) this._requestPostVideoSlotAndTransfer({ localId, file });
    else this._requestUploadSlotAndTransfer({ localId, file, episodeId, mediaType, dynamic, triggerEvent });

    return localId;
  }

  // The post video upload for a block, if the manager has one. Lets a node view that is built after the
  // creator left and came back pick its upload up again.
  getPostVideoUpload(postId, blockId) {
    let found = null;
    this.uploads.forEach((upload) => {
      if (isPostVideo(upload) && String(upload.postId) === String(postId) && upload.blockId === blockId) found = { ...upload };
    });
    return found;
  }

  // Re-fetches fresh Bunny credentials (no true byte-level resume) and restarts the transfer for
  // an existing registry entry. Requires the original `file` to still be cached in memory (i.e.
  // the tab was never closed/reloaded) - callers should fall back to a file picker + `start()`
  // otherwise.
  retry(localId) {
    const existing = this.uploads.get(localId);
    if (!existing || !existing.file) return false;

    this._upsert(localId, {
      state: "uploading",
      bytesUploaded: 0,
      bytesTotal: existing.file.size,
      errorMessage: null,
    });
    this._notify({ type: "retry", upload: this.uploads.get(localId) });

    if (isPostVideo(existing)) {
      this._requestPostVideoSlotAndTransfer({ localId, file: existing.file });
      return true;
    }

    this._requestUploadSlotAndTransfer({
      localId,
      file: existing.file,
      episodeId: existing.episodeId,
      mediaType: existing.mediaType,
    });

    return true;
  }

  cancel(localId) {
    const existing = this.uploads.get(localId);
    if (!existing) return;

    this.ensureWorker().then((worker) => {
      if (worker) worker.postMessage({ type: "cancel", localId });
    });

    if (existing.transportCredentials && existing.transportCredentials.upload_mode === "multipart") {
      this._abortMultipartUpload(existing);
    }

    // Without this, a still-running AWS status poll's closure would resurrect a deleted entry via
    // _upsert()'s "or create" fallback the next time it ticks.
    this._stopAwsStatusPolling(localId);

    this.uploads.delete(localId);
    this._notify({ type: "cancel", upload: { ...existing } });
  }

  // Best-effort, fire-and-forget - so S3 doesn't retain the in-progress object's uploaded parts
  // If this never reaches the server (e.g. the tab closes before the request lands), the
  // `abort_incomplete_multipart_upload` S3 lifecycle rule (Terraform) is the backstop.
  _abortMultipartUpload(upload) {
    const { upload_id: uploadId } = upload.transportCredentials || {};
    if (!uploadId) return;

    if (isPostVideo(upload)) {
      jsonRequest(`${postVideoMemberUrl(upload)}/abort`, { body: { upload_id: uploadId } }).catch(() => {});
      return;
    }

    if (!upload.episodeId) return;

    const formData = new FormData();
    formData.append("upload_id", uploadId);
    formData.append("upload_log_id", upload.uploadLogId);

    csrfFetch(`/dashboard/channels/episodes/${upload.episodeId}/abort_multipart_upload`, {
      method: "POST",
      headers: { Accept: "application/json" },
      body: formData,
    }).catch((error) => {
      // eslint-disable-next-line no-console
      console.error("SupercastUploadManager: failed to abort multipart upload", error);
    });
  }

  // Pauses an in-progress AWS multipart (video) upload. The worker stops launching new parts
  // after the currently in-flight ones complete at their natural part boundaries, so no partial
  // bytes are wasted. The upload record stays in "paused" state and can be resumed later.
  pause(localId) {
    const upload = this.uploads.get(localId);
    if (!upload || upload.state !== "uploading") return;
    if (upload.provider !== "aws" || upload.transportCredentials?.upload_mode !== "multipart") return;

    this._upsert(localId, { state: "paused" });
    this._notify({ type: "paused", upload: this.uploads.get(localId) });

    this.ensureWorker().then((worker) => {
      if (worker) worker.postMessage({ type: "pause", localId });
    });
  }

  // Resumes a paused AWS multipart upload, continuing from the part cursor the worker left off at.
  resume(localId) {
    const upload = this.uploads.get(localId);
    if (!upload || upload.state !== "paused") return;

    this._upsert(localId, { state: "uploading" });
    this._notify({ type: "resumed", upload: this.uploads.get(localId) });

    this.ensureWorker().then((worker) => {
      if (worker) worker.postMessage({ type: "resume", localId });
    });
  }

  // Evicts a terminally-completed record so its (potentially large) cached File/Blob isn't
  // retained for the rest of the tab's lifetime - a `completed` upload never needs retry(), so
  // there's nothing left to keep it around for. Unlike cancel(), this never touches the worker
  // and doesn't emit a `cancel` event (the upload didn't get cancelled, it's just done).
  forget(localId) {
    this.uploads.delete(localId);
  }

  // A local file's { duration (seconds), height, width } from its metadata; height/width are 0 for audio.
  // All 0 when it can't be read (unsupported codec, error, timeout).
  readMetadata(file, timeoutMs = 10000) {
    return new Promise((resolve) => {
      const objectURL = URL.createObjectURL(file);
      const mediaEl = document.createElement(file.type.startsWith("video/") ? "video" : "audio");
      const finish = (duration = 0, height = 0, width = 0) => {
        clearTimeout(timer);
        mediaEl.onloadedmetadata = null;
        mediaEl.onerror = null;
        mediaEl.removeAttribute("src");
        URL.revokeObjectURL(objectURL);
        resolve({ duration: duration > 0 && isFinite(duration) ? duration : 0, height, width });
      };
      const timer = setTimeout(() => finish(), timeoutMs);
      mediaEl.preload = "metadata";
      mediaEl.onloadedmetadata = () => finish(mediaEl.duration, mediaEl.videoHeight, mediaEl.videoWidth);
      mediaEl.onerror = () => finish();
      mediaEl.src = objectURL;
    });
  }

  // The metadata the caller already read, else read it from the file. Duration is 0 when it can't be read.
  async _resolveMetadata(localId, file) {
    const upload = this.uploads.get(localId);
    // durationRead: the caller already tried readMetadata, so a missing duration means it can't be read - don't try again
    if (upload?.duration > 0 || upload?.durationRead) {
      return { duration: Math.ceil(upload.duration || 0), height: upload.height || 0, width: upload.width || 0 };
    }

    const metadata = await this.readMetadata(file);
    if (metadata.duration === 0) return metadata;

    this._upsert(localId, metadata);
    return { ...metadata, duration: Math.ceil(metadata.duration) };
  }

  async _requestUploadSlotAndTransfer({ localId, file, episodeId, mediaType, dynamic, triggerEvent }) {
    const worker = await this.ensureWorker();
    if (!worker) {
      this._upsert(localId, { state: "error", errorMessage: "Background uploads are not enabled for this channel" });
      this._notify({ type: "upload-error", upload: this.uploads.get(localId) });
      return;
    }

    const url = new URL(`${window.location.origin}/dashboard/channels/episodes/presigned_upload`);
    url.searchParams.append("type", mediaType);
    url.searchParams.append("title", file.name);
    url.searchParams.append("size", file.size); //only video's AWS branch actually uses this, to compute the multipart part count server-side
    if (episodeId) url.searchParams.append("episode", episodeId);
    const { duration, height, width } = await this._resolveMetadata(localId, file);
    if (mediaType === "video" && duration > 0) url.searchParams.append("duration", duration);
    // browser-reported source size, so the server can skip a 4K encode of a smaller source
    if (mediaType === "video" && height > 0) url.searchParams.append("height", height);
    if (mediaType === "video" && width > 0) url.searchParams.append("width", width);
    if (dynamic !== undefined) url.searchParams.append("dynamic", dynamic);
    if (triggerEvent !== undefined) url.searchParams.append("trigger_event", triggerEvent);

    let data;
    let response;
    try {
      response = await fetch(url.toString());
      data = await response.json();
    } catch (error) {
      this._upsert(localId, { state: "error", errorMessage: "Could not start upload" });
      this._notify({ type: "upload-error", upload: this.uploads.get(localId) });
      this._dispatchToast("error", `Couldn't start uploading "${file.name}".`);
      return;
    }

    // The server refused the slot (e.g. the monthly video limit): show its message and stop.
    if (!response.ok && data && data.error) {
      const message = data.message || `Couldn't start uploading "${file.name}".`;
      this._upsert(localId, { state: "error", errorMessage: message });
      this._notify({ type: "upload-error", upload: this.uploads.get(localId) });
      this._dispatchToast("error", message);
      return;
    }

    const { episode_id: resolvedEpisodeId, upload_log_id: uploadLogId, provider, ...credentials } = data;

    this._upsert(localId, {
      episodeId: resolvedEpisodeId,
      uploadLogId,
      provider,
      transportCredentials: credentials,
    });
    this._notify({ type: "episode-resolved", upload: this.uploads.get(localId) });

    logUploadState(resolvedEpisodeId, uploadLogId, "upload_started");

    worker.postMessage({ type: "start", localId, file, provider, credentials, uploadLogId });
  }

  // Post video counterpart of _requestUploadSlotAndTransfer: creates the pending asset on the post and
  // hands the worker its presigned parts. The asset id is announced ("slot-ready") as soon as it exists so
  // the editor block can store it right away - the post then keeps its video even if the creator leaves.
  async _requestPostVideoSlotAndTransfer({ localId, file }) {
    const worker = await this.ensureWorker();
    const upload = this.uploads.get(localId);
    if (!upload) return;
    if (!worker) {
      this._upsert(localId, { state: "error", errorMessage: "Couldn't start the upload worker" });
      this._notify({ type: "upload-error", upload: this.uploads.get(localId) });
      return;
    }

    let result = null;
    try {
      result = await jsonRequest(upload.videosPath, { body: { filename: file.name, size: file.size } });
    } catch (error) {
      result = null;
    }

    if (!this.uploads.has(localId)) return; // cancelled while the slot was being created

    if (!result || !result.response.ok) {
      const message = (result && result.data.error) || "Could not start upload";
      this._upsert(localId, { state: "error", errorMessage: message });
      this._notify({ type: "upload-error", upload: this.uploads.get(localId) });
      this._dispatchToast("error", `Couldn't start uploading "${file.name}".`);
      return;
    }

    const { external_id: externalId, ...credentials } = result.data;
    this._upsert(localId, { externalId, provider: "aws", transportCredentials: { upload_mode: "multipart", ...credentials } });
    this._notify({ type: "slot-ready", upload: this.uploads.get(localId) });

    worker.postMessage({ type: "start", localId, file, provider: "aws", credentials: this.uploads.get(localId).transportCredentials });
  }

  _handleWorkerMessage(data) {
    const upload = this.uploads.get(data.localId);
    if (!upload) return;

    switch (data.type) {
      case "progress":
        this._upsert(data.localId, { bytesUploaded: data.bytesUploaded, bytesTotal: data.bytesTotal });
        this._notify({ type: "progress", upload: this.uploads.get(data.localId) });
        break;
      case "upload-complete":
        this._upsert(data.localId, { state: "processing", bytesUploaded: upload.bytesTotal });
        logUploadState(upload.episodeId, upload.uploadLogId, "processing_started");
        this._notify({ type: "processing", upload: this.uploads.get(data.localId) });
        // AWS: current_audio_media_asset/current_video_media_asset was already pointed at
        // server-side in presigned_upload, and readiness is driven by the S3-event webhook
        // (Aws::UploadLandedService) / the MediaConvert pipeline, not by the client - so there is
        // no podcast_audio/podcast_video PATCH to send for this provider.
        if (upload.provider === "aws") {
          if (upload.transportCredentials && upload.transportCredentials.upload_mode === "multipart") {
            // Video/multipart: S3 won't assemble the object until we tell it which parts to use,
            // so this must happen (and succeed) before metadata-submit/status-polling start.
            this._completeMultipartUpload(data.localId, data.parts);
          } else {
            this._submitAwsMetadata(data.localId);
            this._startAwsStatusPolling(data.localId);
          }
        } else {
          this._commitUpload(data.localId);
        }
        break;
      case "paused":
        // Worker confirmed the part pool drained cleanly - state is already "paused" (set
        // optimistically in pause()), so this is a no-op. Kept for parity with "resumed".
        break;
      case "upload-error":
        this._upsert(data.localId, { state: "error", errorMessage: data.message });
        // Worker-supplied diagnostics (AWS multipart and single-PUT) go to the log only, not the UI message.
        logUploadState(
          upload.episodeId,
          upload.uploadLogId,
          "error",
          data.diagnostics ? `${data.message} [${data.diagnostics}]` : data.message,
        );
        this._notify({ type: "upload-error", upload: this.uploads.get(data.localId) });
        this._dispatchToast("error", `Upload failed for "${upload.file?.name || "your file"}".`);
        break;
      default:
        break;
    }
  }

  async _commitUpload(localId) {
    const upload = this.uploads.get(localId);
    if (!upload) return;

    const formData = new FormData();

    const isVideo = upload.mediaType === "video";
    const metadata = {
      size: upload.file.size,
      filename: upload.file.name,
      mime_type: upload.file.type,
      duration: upload.duration,
    };

    const mediaId = mediaIdFor(upload.provider, upload.transportCredentials);

    if (isVideo) {
      formData.append("episode[podcast_video]", mediaId);
      formData.append("episode[podcast_video_meta]", JSON.stringify(metadata));
    } else {
      formData.append("episode[podcast_audio]", mediaId);
      formData.append("episode[podcast_audio_meta]", JSON.stringify(metadata));
    }
    formData.append("upload_log_id", upload.uploadLogId);

    const payload = { method: "PATCH", headers: { Accept: "application/json" }, body: formData };

    let attempts = 0;
    let response = null;
    while (attempts < 3) {
      try {
        // eslint-disable-next-line no-await-in-loop
        response = await csrfFetch(`/dashboard/channels/episodes/${upload.episodeId}`, payload);
        if (response.ok) break;
      } catch (error) {
        response = null;
      }
      attempts++;
    }

    if (!response || !response.ok) {
      this._failCommit(localId, `Commit failed with status ${response ? response.status : "network error"}`);
      return;
    }

    this._upsert(localId, { state: "completed" });
    this._notify({ type: "commit-success", upload: this.uploads.get(localId) });
    this._dispatchToast("success", `"${upload.file.name}" finished uploading.`);
  }

  // Shared failure UI state for anything that finishes the byte transfer but then fails to
  // finalize the upload server-side
  _failCommit(localId, message) {
    const upload = this.uploads.get(localId);
    if (!upload) return;

    this._upsert(localId, { state: "error", errorMessage: message });
    logUploadState(upload.episodeId, upload.uploadLogId, "error", message);
    this._notify({ type: "commit-error", upload: this.uploads.get(localId) });
    this._dispatchToast("error", `Couldn't finish uploading "${upload.file.name}".`);
  }

  // AWS video/multipart-only: tells S3 which uploaded parts (by ETag) to assemble into the final
  // object. Must succeed before _submitAwsMetadata/_startAwsStatusPolling run.
  //
  // Also carries duration, written server-side before this S3 call completes the object - not
  // left to the later, un-awaited _submitAwsMetadata POST. That POST is a full browser round trip
  // racing a pure server-to-server one (the S3 completion below triggers the "Object Created"
  // webhook straight into UploadLandedService#claim_video_minutes), and if the webhook wins,
  // there will be no duration to claim.
  async _completeMultipartUpload(localId, parts) {
    const upload = this.uploads.get(localId);
    if (!upload) return;

    const { upload_id: uploadId } = upload.transportCredentials || {};
    const { duration } = await this._resolveMetadata(localId, upload.file);

    let response = null;
    try {
      if (isPostVideo(upload)) {
        const body = { upload_id: uploadId, parts };
        if (duration > 0) body.duration = duration;
        ({ response } = await jsonRequest(`${postVideoMemberUrl(upload)}/complete`, { body }));
      } else {
        const formData = new FormData();
        formData.append("upload_id", uploadId);
        if (duration > 0) formData.append("duration", duration);
        (parts || []).forEach((part) => {
          formData.append("parts[][part_number]", part.part_number);
          formData.append("parts[][etag]", part.etag);
        });

        response = await csrfFetch(`/dashboard/channels/episodes/${upload.episodeId}/complete_multipart_upload`, {
          method: "POST",
          headers: { Accept: "application/json" },
          body: formData,
        });
      }
    } catch (error) {
      response = null;
    }

    if (!response || !response.ok) {
      this._failCommit(localId, `Failed to complete upload with status ${response ? response.status : "network error"}`);
      return;
    }

    this._submitAwsMetadata(localId);

    // From here it's purely a server-side MediaConvert wait driven by webhook/poll - the tab no
    // longer matters, so stop warning on beforeunload.
    this._upsert(localId, { state: "transcoding" });
    this._notify({ type: "transcoding", upload: this.uploads.get(localId) });

    this._startAwsStatusPolling(localId);
  }

  // AWS-only counterpart to _commitUpload()'s podcast_audio_meta PATCH: submits the same
  // {size, filename, mime_type, duration} shape to the dedicated media_asset_metadata endpoint,
  // since AWS uploads never PATCH podcast_audio (see the upload-complete handler above). Best-
  // effort - a failure here only means the dashboard duration display stays at 0:00, it doesn't
  // block the upload from completing (that's governed entirely by _startAwsStatusPolling).
  async _submitAwsMetadata(localId) {
    const upload = this.uploads.get(localId);
    if (!upload) return;

    if (isPostVideo(upload)) {
      try {
        await jsonRequest(postVideoMemberUrl(upload), {
          method: "PATCH",
          body: { size: upload.file.size, filename: upload.file.name, mime_type: upload.file.type, duration: upload.duration },
        });
      } catch (error) {
        // Best effort, like the episode metadata below.
      }
      return;
    }

    const formData = new FormData();
    formData.append("media_type", upload.mediaType);
    formData.append("size", upload.file.size);
    formData.append("filename", upload.file.name);
    formData.append("mime_type", upload.file.type);
    formData.append("duration", upload.duration);

    try {
      await csrfFetch(`/dashboard/channels/episodes/${upload.episodeId}/media_asset_metadata`, {
        method: "POST",
        headers: { Accept: "application/json" },
        body: formData,
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("SupercastUploadManager: failed to submit AWS media asset metadata", error);
    }
  }

  _startAwsStatusPolling(localId) {
    const upload = this.uploads.get(localId);
    if (!upload || upload.awsStatusPollTimer) return;

    const successStatus = "complete";
    const failureStatus = "error";

    const poll = async () => {
      let data;
      try {
        const url = isPostVideo(upload)
          ? postVideoMemberUrl(upload)
          : `/dashboard/channels/episodes/${upload.episodeId}/media_asset_status?media_type=${upload.mediaType}`;
        const response = await fetch(url, { headers: { Accept: "application/json" }, credentials: "include" });
        if (!response.ok) return;
        data = await response.json();
      } catch (error) {
        return;
      }

      // A post video's status endpoint reports { ready, status } rather than the episode's "complete".
      if (isPostVideo(upload)) {
        if (data.ready) data = { status: successStatus };
        else if (POST_VIDEO_FAILED_STATUSES.includes(data.status)) data = { status: failureStatus };
      }

      if (data.status === successStatus) {
        this._stopAwsStatusPolling(localId);
        this._upsert(localId, { state: "completed" });
        this._notify({ type: "commit-success", upload: this.uploads.get(localId) });
        this._dispatchToast("success", `"${upload.file.name}" finished uploading.`);
      } else if (data.status === failureStatus) {
        const message = "Upload failed to process";
        this._stopAwsStatusPolling(localId);
        this._upsert(localId, { state: "error", errorMessage: message });
        logUploadState(upload.episodeId, upload.uploadLogId, "error", message);
        this._notify({ type: "upload-error", upload: this.uploads.get(localId) });
        this._dispatchToast("error", `Upload failed for "${upload.file.name}".`);
      }
    };

    poll();
    this._upsert(localId, { awsStatusPollTimer: setInterval(poll, 4000) });
  }

  _stopAwsStatusPolling(localId) {
    const upload = this.uploads.get(localId);
    if (!upload || !upload.awsStatusPollTimer) return;

    clearInterval(upload.awsStatusPollTimer);
    this._upsert(localId, { awsStatusPollTimer: null });
  }

  _dispatchToast(type, message) {
    window.dispatchEvent(new CustomEvent("supercast:upload-toast", { detail: { type, message } }));
  }

  _upsert(localId, patch) {
    const existing = this.uploads.get(localId) || { localId };
    this.uploads.set(localId, { ...existing, ...patch });
  }

  _notify(event) {
    this.listeners.forEach((listener) => {
      try {
        listener(event);
      } catch (error) {
        // A misbehaving subscriber (e.g. a Stimulus controller that already disconnected) must
        // not break uploads for every other listener.
        // eslint-disable-next-line no-console
        console.error("SupercastUploadManager listener error", error);
      }
    });
  }
}

window.SupercastUploadManager = window.SupercastUploadManager || new EpisodeUploadManager();

// A page reload/close terminates this page's Worker outright (no browser API lets a plain
// Worker survive that) - warn before it happens rather than silently losing an in-flight
// transfer. Only fires while something is actually mid-transfer; already-completed/errored
// uploads have nothing left to lose. Registered once per real page load (this module only
// evaluates once - Turbo's in-app soft navigation doesn't re-run it, only an actual
// unload/reload does, which is exactly what this needs to catch).
window.addEventListener("beforeunload", (event) => {
  const hasActiveUpload = window.SupercastUploadManager.getAllUploads().some(
    (upload) => upload.state === "uploading" || upload.state === "processing" || upload.state === "paused"
  );
  if (!hasActiveUpload) return;

  event.preventDefault();
  event.returnValue = ""; // the actual text shown is browser-defined
});

export default window.SupercastUploadManager;
